import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import programAPI from '../services/programApi';
import {
    Box, Typography, Card, CardContent, Button, CircularProgress,
    CardMedia, IconButton, useTheme, Drawer, useMediaQuery, Fab,
    Dialog, DialogTitle, DialogContent, DialogActions, Zoom, Grid,
    Snackbar, Alert, Divider, Chip
} from '@mui/material';
import {
    FitnessCenter, Menu as MenuIcon, Close,
    AccessTime, TrendingUp, Restaurant, Article, Close as CloseIcon
} from '@mui/icons-material';
import UserNavbar from '../components/UserNavbar';
import SideMenu from '../components/SideMenu';
import Footer from "../components/Footer";

const UserBuyPrograms = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const [programs, setPrograms] = useState([]);
    const [myPrograms, setMyPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewProgram, setViewProgram] = useState(null);
    const [paymentDialog, setPaymentDialog] = useState({ open: false, program: null });
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [selectedMenuItem, setSelectedMenuItem] = useState(null);

    useEffect(() => {
        fetchPrograms();
        fetchMyPrograms();
    }, []);

    const fetchPrograms = async () => {
        try {
            setLoading(true);
            const data = await programAPI.getAll();
            setPrograms(data);
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const fetchMyPrograms = async () => {
        try {
            const data = await programAPI.getMyPrograms();
            setMyPrograms(data);
        } catch (err) { setMyPrograms([]); }
    };

    const handleBuyClick = (program, e) => {
        if (e) e.stopPropagation();
        setPaymentDialog({ open: true, program });
        setViewProgram(null);
    };

    // ✅ Истото странично мени како во UserDashboard / ProgramsPage
    const menuItems = [
        {
            id: 1,
            title: 'Купи Програми',
            description: 'Прегледај и купи тренинг програми креирани од експерти за максимални резултати',
            icon: <FitnessCenter sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/buy-programs',
            color: '#ff7eb9',
            available: true,
            badge: 'Популарно'
        },
        {
            id: 2,
            title: 'Купи Диета',
            description: 'Здравствени диететски планови прилагодени за вашите фитнес цели',
            icon: <Restaurant sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-diets',
            color: '#4CAF50',
            available: true,
            badge: 'Препорачано'
        },
        {
            id: 3,
            title: 'Блог',
            description: 'Читај статии, совети и информации за фитнес и здравје',
            icon: <Article sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-blog',
            color: '#a5d8ff',
            available: true,
            badge: 'Ново'
        },
        {
            id: 4,
            title: 'Достигнувања',
            description: 'Следете ги вашите достигнувања, прогресија и фитнес милници',
            icon: <TrendingUp sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/achievements',
            color: '#ffd166',
            available: false,
            badge: 'Наскоро'
        }
    ];

    const userName = user?.email
        ? user.email
        : (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Корисник');

    const handleMenuItemClick = (item) => {
        setSelectedMenuItem(item);
        if (isMobile) setMobileMenuOpen(false);
        if (!item.available) {
            alert('🚧 Оваа функционалност е во развој и наскоро ќе биде достапна!');
            return;
        }
        navigate(item.path);
    };

    const handleMenuItemHover = (item) => {
        if (!isMobile) setSelectedMenuItem(item);
    };

    // ✅ Позадината сега реагира на dark mode
    const pageBackground = isDarkMode
        ? 'linear-gradient(135deg, #1a1a1a 0%, #241b2e 100%)'
        : 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)';

    const modalBackground = isDarkMode
        ? 'linear-gradient(135deg, #1a1a1a 0%, #241b2e 100%)'
        : 'linear-gradient(135deg, #ffeef8 0%, #e0f7ff 100%)';

    if (loading) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress sx={{ color: '#ff7eb9' }} size={60} />
                </Box>
            </>
        );
    }

    return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                <UserNavbar />
            </Box>

            <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>

                {/* Странично мени - Desktop */}
                {!isMobile && (
                    <Box sx={{
                        width: '320px',
                        backgroundColor: 'background.paper',
                        borderRight: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                        position: 'sticky',
                        top: '64px',
                        height: 'calc(100vh - 64px)',
                        overflowY: 'auto',
                        zIndex: 999
                    }}>
                        <SideMenu
                            menuItems={menuItems}
                            selectedItem={selectedMenuItem}
                            onItemClick={handleMenuItemClick}
                            onItemHover={handleMenuItemHover}
                            programsCount={myPrograms.length}
                            userName={userName}
                        />
                    </Box>
                )}

                {/* Странично мени - Mobile (Drawer + Fab) */}
                {isMobile && (
                    <>
                        <Drawer
                            anchor="left"
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            PaperProps={{
                                sx: {
                                    width: '280px',
                                    backgroundColor: 'background.paper',
                                    borderRight: 1,
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => setMobileMenuOpen(false)}>
                                    <Close />
                                </IconButton>
                            </Box>
                            <SideMenu
                                menuItems={menuItems}
                                selectedItem={selectedMenuItem}
                                onItemClick={handleMenuItemClick}
                                onItemHover={handleMenuItemHover}
                                programsCount={myPrograms.length}
                                userName={userName}
                            />
                        </Drawer>
                        <Fab
                            color="primary"
                            onClick={() => setMobileMenuOpen(true)}
                            sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000 }}
                        >
                            <MenuIcon />
                        </Fab>
                    </>
                )}

                {/* Главна содржина */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <Box sx={{ background: pageBackground, minHeight: '100%', pt: { xs: 8, md: 6 }, pb: 10 }}>
                        <Box sx={{ px: { xs: 2, md: 6 } }}>

                            <Box textAlign="center" mb={8}>
                                <Typography variant="h1" sx={{
                                    fontWeight: 900,
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontSize: { xs: '2.5rem', md: '3.5rem' }
                                }}>
                                    Нашите Тренинг Програми
                                </Typography>
                                <Box sx={{ width: 60, height: 4, bgcolor: '#ff7eb9', mx: 'auto', mt: 2 }} />
                            </Box>

                            <Grid container spacing={4} alignItems="stretch">
                                {programs.map((program) => (
                                    <Grid item xs={12} sm={6} md={4} key={program.id} sx={{ display: 'flex' }}>
                                        <Card
                                            onClick={() => setViewProgram(program)}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: 'pointer',
                                                borderRadius: '20px',
                                                bgcolor: 'background.paper',
                                                transition: 'all 0.3s ease',
                                                '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 12px 30px rgba(255,126,185,0.2)' }
                                            }}
                                        >
                                            <Box sx={{ height: 6, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                                                <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 800, mb: 2, fontSize: '1.4rem' }}>{program.name}</Typography>

                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        mb: 3,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {program.description || ''}
                                                </Typography>

                                                <Box sx={{ mt: 'auto' }}>
                                                    <Typography variant="h5" sx={{ color: '#ff7eb9', fontWeight: 900, mb: 2 }}>{program.price} ден</Typography>
                                                    <Button
                                                        variant="contained"
                                                        fullWidth
                                                        onClick={(e) => handleBuyClick(program, e)}
                                                        sx={{ borderRadius: '12px', bgcolor: '#ff7eb9', py: 1.5, fontWeight: 'bold' }}
                                                    >
                                                        🛒 Купете тука
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>

                    <Footer />
                </Box>
            </Box>

            {/* Full Screen Modal - Детали за програмата */}
            <Dialog
                fullScreen
                open={!!viewProgram}
                onClose={() => setViewProgram(null)}
                TransitionComponent={Zoom}
            >
                <Box sx={{
                    minHeight: '100vh',
                    background: modalBackground,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    pt: { xs: 20, md: 30 }
                }}>
                    {viewProgram && (
                        <Card sx={{
                            maxWidth: 800,
                            width: '100%',
                            borderRadius: '30px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                            overflow: 'visible',
                            position: 'relative',
                            bgcolor: 'background.paper'
                        }}>
                            <IconButton
                                onClick={() => setViewProgram(null)}
                                sx={{
                                    position: 'absolute',
                                    top: 15,
                                    right: 15,
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    color: '#ff7eb9',
                                    '&:hover': { bgcolor: '#ff7eb9', color: 'white' },
                                    zIndex: 10,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            >
                                <CloseIcon fontSize="medium" />
                            </IconButton>

                            <Box sx={{ height: 10, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)', borderRadius: '30px 30px 0 0' }} />

                            <CardContent sx={{ p: { xs: 4, md: 8 } }}>
                                <Chip label="Детали за програмата" sx={{ mb: 3, bgcolor: '#ff7eb9', color: 'white' }} />
                                <Typography variant="h2" sx={{ fontWeight: 900, mb: 3, color: 'text.primary', fontSize: { xs: '2rem', md: '3rem' }, pr: 5 }}>
                                    {viewProgram.name}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '1.1rem',
                                        lineHeight: 1.8,
                                        color: 'text.secondary',
                                        mb: 4,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {viewProgram.description || ''}
                                </Typography>

                                <Grid container spacing={2} sx={{ mb: 4 }}>
                                    <Grid item>
                                        <Chip label={`⏱️ Траење: ${viewProgram.durationDays} денови`} variant="outlined" />
                                    </Grid>
                                    <Grid item>
                                        <Chip label={`📋 Вежби: ${viewProgram.days?.length || 0}`} variant="outlined" />
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#ff7eb9' }}>{viewProgram.price} ден</Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={(e) => handleBuyClick(viewProgram, e)}
                                        sx={{ px: 6, py: 2, borderRadius: '15px', bgcolor: '#ff7eb9', fontWeight: 'bold' }}
                                    >
                                        Купи ја програмата
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Dialog>

            {/* PAYMENT INSTRUCTIONS DIALOG - ✅ поправено, theme-aware */}
            <Dialog open={paymentDialog.open} onClose={() => setPaymentDialog({ open: false, program: null })} fullWidth maxWidth="sm">
                <DialogTitle sx={{ bgcolor: '#ff7eb9', color: 'white' }}>
                    Начин на плаќање
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
                    <Typography variant="body1" mb={2} sx={{ color: 'text.primary' }}>
                        За да ја купите програмата <strong>{paymentDialog.program?.name}</strong>, извршете уплата од <strong>{paymentDialog.program?.price} ден.</strong> на следната сметка:
                    </Typography>

                    <Box sx={{
                        bgcolor: 'background.default',
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography sx={{ color: 'text.primary' }}><strong>Примач:</strong> Anamarija Angelkoska</Typography>
                        <Typography sx={{ color: 'text.primary' }}><strong>Трансакциска сметка:</strong> 210501755493342</Typography>
                        <Typography sx={{ color: 'text.primary' }}><strong>Банка:</strong> НЛБ Банка</Typography>
                    </Box>

                    <Typography variant="body2" mt={2} color="error" fontWeight="bold">
                        ВАЖНО: Во "Цел на дознака" задолжително наведете:
                    </Typography>
                    <Typography variant="body2" sx={{
                        bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.15)' : '#fff3f3',
                        color: 'text.primary',
                        p: 1,
                        mt: 1,
                        borderRadius: 1
                    }}>
                        {paymentDialog.program?.name} - Вашиот Емаил
                    </Typography>

                    <Typography variant="body2" mt={2} sx={{ color: 'text.secondary' }}>
                        Откако ќе ја извршите уплатата, админот ќе ја провери вашата трансакција и ќе ви овозможи пристап во најкраток можен рок.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ bgcolor: 'background.paper' }}>
                    <Button onClick={() => setPaymentDialog({ open: false, program: null })}>Затвори</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UserBuyPrograms;